import React, { useState, useEffect } from "react";
import {
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
} from "@fluentui/react";
import SaveButton from "@/ui-controls/SaveButton";
import CancelButton from "@/ui-controls/CancelButton";
import TextInput from "@/ui-controls/TextInput";
import { Grid } from "fluentui-react-grid";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  IntegrationTagDto,
  createIntegration,
  createIntegrationTagMapping,
  listIntegrationTags,
} from "@/api/integrations";

interface Props {
  onSave?: () => void;
  onClose?: () => void;
}

const NewIntegrationForm = (props: Props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [tags, setTags] = useState<IntegrationTagDto[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  useEffect(() => {
    listIntegrationTags(undefined, 1000)
      .then((data) => setTags(data?.result || []))
      .catch(() => setTags([]));
  }, []);

  const tagOptions: IDropdownOption[] = tags
    .filter((t) => t.isActive)
    .map((t) => ({ key: t.id, text: t.name }));

  const formik = useFormik({
    initialValues: { code: "", name: "" },
    validationSchema: Yup.object({
      code: Yup.string().required("Code is required").max(50),
      name: Yup.string().required("Name is required").max(250),
    }),
    onSubmit: async (values) => {
      setIsSaving(true);
      setErrorMessage("");
      try {
        const integrationId = await createIntegration({
          code: values.code,
          name: values.name,
        });

        // Create tag mappings for selected tags
        await Promise.all(
          selectedTagIds.map((tagId) =>
            createIntegrationTagMapping({
              integrationId,
              integrationTagId: tagId,
            })
          )
        );

        props.onSave?.();
      } catch (error: any) {
        const data = error?.response?.data;
        if (data?.detail) {
          setErrorMessage(data.detail);
        } else {
          setErrorMessage("Failed to create integration.");
        }
      } finally {
        setIsSaving(false);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid dir="ltr">
        {errorMessage && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <MessageBar
                messageBarType={MessageBarType.error}
                onDismiss={() => setErrorMessage("")}
              >
                {errorMessage}
              </MessageBar>
            </Grid.Col>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <TextInput
              label="Code"
              baseTypeProps={{
                disabled: isSaving,
                ...formik.getFieldProps("code"),
              }}
              hasError={formik.errors.code && formik.touched.code}
              errorMessage={formik.errors.code}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <TextInput
              label="Name"
              baseTypeProps={{
                disabled: isSaving,
                ...formik.getFieldProps("name"),
              }}
              hasError={formik.errors.name && formik.touched.name}
              errorMessage={formik.errors.name}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <Dropdown
              label="Tags"
              placeholder="Select tags"
              options={tagOptions}
              multiSelect
              disabled={isSaving}
              selectedKeys={selectedTagIds}
              onChange={(_, option) => {
                if (option) {
                  setSelectedTagIds((prev) =>
                    option.selected
                      ? [...prev, option.key as number]
                      : prev.filter((k) => k !== option.key)
                  );
                }
              }}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row className="panel-buttons">
          <Grid.Col>
            <SaveButton
              text="Save"
              baseTypeProps={{ type: "submit", disabled: isSaving }}
            />
          </Grid.Col>
          <Grid.Col>
            <CancelButton
              text="Cancel"
              onClick={() => props.onClose?.()}
              baseTypeProps={{ disabled: isSaving }}
            />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </form>
  );
};

export default NewIntegrationForm;
